import React from 'react';
import {
  MVX_RANGE_PROVENANCE,
  MVX_HOW_TO_READ,
  MVX_PROVENANCE_HEADING,
} from '../../models/mvxReferences';

/**
 * MVX "Range References" tab — where every colored band on every MVX bar comes
 * from, one row per band per marker group.
 *
 * ITS OWN TAB, not a block inside References. The two answer different
 * questions: References is the literature behind the test, and is read once;
 * this is read with a bar on screen, to settle "where did that green edge come
 * from" about the marker in front of you. Sharing a tab meant scrolling a page
 * of citations to reach the table, and the table's own heading competed with
 * the citation group titles for the same job.
 *
 * A TABLE rather than prose because the content is a matrix — band against
 * source — and the rows read as eleven unrelated statements when they are
 * really one statement eleven times. It also makes the caveat badges
 * impossible to miss, which matters: several of these bounds are ours and
 * nobody's published figure, and one is waiting on Dr. Schmidt.
 *
 * ROW ORDER IS BAR ORDER (see MVX_RANGE_PROVENANCE), so the reader can go from
 * a bar straight to its row.
 */

/*
 * The band chip: a pale fill, a readable same-hue label, a medium border.
 *
 * This is the Results Overview STATUS_SOFT treatment, copied inline exactly as
 * BMIBarChart's current-band pill copies it — the house answer for a colored
 * chip with WORDS ON TOP of it, as opposed to a wide plot band with nothing on
 * it. The tones sit in the same family as the pale "Markers Outside Optimal"
 * bars rather than the full-strength MUTED_PALETTE the MVX plot bands use,
 * because a chip is small and type has to survive on it.
 *
 * THE LABEL IS NEITHER WHITE NOR BLACK. White on a toned-down red reads at
 * about 1.5:1 and disappears; black on the full-strength #FC7160 was what made
 * the first version look wrong. Each word takes a deep version of its own band
 * color instead, which is legible and still unmistakably that band.
 *
 * Only three of the four statuses appear. Orange is the "> Lab" band on the
 * bars, which is derived from the lab bound rather than sourced, so it has no
 * row of its own.
 */
export const BAND_CHIP: { [band: string]: { bg: string; text: string; border: string } } = {
  Optimal: { bg: '#DEF3E2', text: '#2A7134', border: '#5CB166' },
  Lab: { bg: '#FAF1C2', text: '#7A6912', border: '#D8BE47' },
  Alarm: { bg: '#FAD6CE', text: '#B23322', border: '#E56A5A' },
};

// Column rules between Band|Markers and Markers|Source. A touch darker than
// the row rule, so the columns read as the stronger division of the two.
const COL_RULE = '1px solid #ded5c9';
const ROW_RULE = '1px solid #eee7df';

// Rows are tight: a 3px band top and bottom. The cells are short and there are
// eleven of them, so the generous padding made the table scroll for no reason.
const CELL = '3px 10px';

const badge = (text: string, key: string) => (
  <span
    key={key}
    style={{
      display: 'inline-block',
      marginLeft: 6,
      padding: '0 5px',
      borderRadius: 3,
      background: '#f2ede5',
      color: '#857d73',
      fontSize: 10,
      fontWeight: 700,
      letterSpacing: '0.04em',
      textTransform: 'uppercase',
      whiteSpace: 'nowrap',
    }}
  >
    {text}
  </span>
);

export class MvxRangeReferencesSection extends React.Component {
  render() {
    return (
      <span>
        {/* How to read the bars comes FIRST and sits outside the table: it is
            not a band, and it was previously a row that had to put "Optimal"
            in its Band column and a sentence in its Markers column to be one
            (Maryann, 2026-10-15). */}
        <span className="consideration-title">{MVX_HOW_TO_READ.heading}</span>
        <p style={{ marginTop: 6, marginBottom: 16, lineHeight: 1.5, fontSize: 12 }}>
          {MVX_HOW_TO_READ.text}
        </p>
        <span className="consideration-title">{MVX_PROVENANCE_HEADING}</span>
        <div style={{ overflowX: 'auto', marginTop: 6, marginBottom: 14 }}>
          <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 520, fontSize: 12 }}>
            <thead>
              <tr>
                {['Band', 'Markers', 'Source'].map((h, c) => (
                  <th
                    key={h}
                    style={{
                      textAlign: 'left',
                      padding: CELL,
                      paddingLeft: c === 0 ? 0 : 10,
                      paddingRight: c === 2 ? 0 : 10,
                      borderBottom: '2px solid #cfc7bb',
                      borderRight: c < 2 ? COL_RULE : undefined,
                      color: '#2a2a2a',
                      fontWeight: 700,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {MVX_RANGE_PROVENANCE.map((row, i) => (
                <tr key={i}>
                  <td
                    style={{
                      padding: CELL,
                      paddingLeft: 0,
                      borderBottom: ROW_RULE,
                      borderRight: COL_RULE,
                      verticalAlign: 'top',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    <span
                      style={{
                        display: 'inline-block',
                        padding: '0 7px',
                        borderRadius: 3,
                        background: BAND_CHIP[row.band].bg,
                        border: `1px solid ${BAND_CHIP[row.band].border}`,
                        color: BAND_CHIP[row.band].text,
                        fontWeight: 600,
                      }}
                    >
                      {row.band}
                    </span>
                  </td>
                  <td
                    style={{
                      padding: CELL,
                      borderBottom: ROW_RULE,
                      borderRight: COL_RULE,
                      verticalAlign: 'top',
                      color: '#5c564f',
                    }}
                  >
                    {row.markers}
                  </td>
                  <td
                    style={{
                      padding: CELL,
                      paddingRight: 0,
                      borderBottom: ROW_RULE,
                      verticalAlign: 'top',
                      lineHeight: 1.5,
                      // A source may break its own line. Only the row listing
                      // all six factor ranges does: the list runs to two lines
                      // on its own and three in a narrow window, so it starts
                      // on a line of its own rather than wherever the sentence
                      // in front of it happens to end (Maryann, 2026-10-09).
                      whiteSpace: 'pre-line',
                    }}
                  >
                    {row.source}
                    {/* The study's PMID rides along with the claim, linked, in
                        the same "<text>  —  <identifier>" form the citation
                        list uses. A reader who never opens the References tab
                        otherwise cannot tell which paper the row means. */}
                    {row.identifier && row.url && (
                      <>
                        {'  —  '}
                        <a href={row.url} target="_blank" rel="noopener noreferrer">
                          {row.identifier}
                        </a>
                      </>
                    )}
                    {row.chosen && badge('Chosen by LabSmarts', 'chosen')}
                    {row.ask && badge('Open with Dr. Schmidt', 'ask')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </span>
    );
  }
}

export default MvxRangeReferencesSection;
